'use client';

import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaPlay } from 'react-icons/fa';
import { VideoProject } from '@/app/data/videoProjects';

interface VideoBandProps {
  videos: VideoProject[];
  onVideoClick?: (video: VideoProject) => void;
}

const VideoBand: React.FC<VideoBandProps> = ({ videos, onVideoClick }) => {
  const [hoveredVideo, setHoveredVideo] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [loadedThumbnails, setLoadedThumbnails] = useState<Set<string>>(new Set());
  const [visibleVideos, setVisibleVideos] = useState<Set<string>>(new Set());
  const scrollRef = useRef<HTMLDivElement>(null);
  const videoRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const userScrollTimeoutRef = useRef<NodeJS.Timeout>();

  const handleVideoClick = useCallback((e: React.MouseEvent, video: VideoProject) => {
    e.stopPropagation();
    if (onVideoClick) {
      onVideoClick(video);
    }
  }, [onVideoClick]);

  // Setup Intersection Observer for lazy loading
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const videoId = entry.target.getAttribute('data-video-key');
          if (videoId && entry.isIntersecting) {
            setVisibleVideos((prev) => {
              const newSet = new Set(prev);
              newSet.add(videoId);
              return newSet;
            });
          }
        });
      },
      {
        root: scrollRef.current,
        rootMargin: '100px',
        threshold: 0.01
      }
    );

    // Observe all video containers
    videoRefs.current.forEach((element) => {
      if (element) observer.observe(element);
    });

    return () => {
      observer.disconnect();
    };
  }, [videos]);

  // Handle user scroll detection
  const handleScroll = useCallback(() => {
    setIsUserScrolling(true);
    
    // Clear existing timeout
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }
    
    // Set timeout to resume auto-scroll after user stops scrolling
    userScrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
    }, 3000); // Resume auto-scroll after 3 seconds of no user interaction
  }, []);

  // Auto-scroll functionality with smooth, slow movement
  useEffect(() => {
    const scrollContainer = scrollRef.current;
    if (!scrollContainer || !isLoaded) return;

    let animationId: number;
    const scrollSpeed = 0.8; // Slower for better performance
    let isPaused = false;

    const autoScroll = () => {
      if (!isPaused && !isUserScrolling && scrollContainer) {
        scrollContainer.scrollLeft += scrollSpeed;
        
        // Calculate seamless loop reset point
        // We have 4 copies, so reset when we've scrolled past the first copy
        const singleSetWidth = scrollContainer.scrollWidth / 4;
        if (scrollContainer.scrollLeft >= singleSetWidth) {
          scrollContainer.scrollLeft = 0;
        }
      }
      animationId = requestAnimationFrame(autoScroll);
    };

    // Start scrolling after ensuring DOM is ready
    const startDelay = setTimeout(() => {
      autoScroll();
    }, 1000); // Increased delay for better initial load

    // Pause on hover
    const handleMouseEnter = () => { isPaused = true; };
    const handleMouseLeave = () => { isPaused = false; };

    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);
    scrollContainer.addEventListener('wheel', handleScroll, { passive: true });
    scrollContainer.addEventListener('touchstart', handleScroll, { passive: true });
    scrollContainer.addEventListener('touchmove', handleScroll, { passive: true });

    return () => {
      clearTimeout(startDelay);
      cancelAnimationFrame(animationId);
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
      if (scrollContainer) {
        scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
        scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
        scrollContainer.removeEventListener('wheel', handleScroll);
        scrollContainer.removeEventListener('touchstart', handleScroll);
        scrollContainer.removeEventListener('touchmove', handleScroll);
      }
    };
  }, [isLoaded, isUserScrolling, handleScroll]);

  // Create enough copies for smooth infinite scroll
  const infiniteVideos = [...videos, ...videos, ...videos, ...videos];

  // Mark as loaded after component mounts
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="w-full bg-black">
      {/* California dreamy section header */}
      <div className="text-center py-12">
        <h2 className="text-4xl md:text-5xl font-light text-white mb-4 tracking-wide">
          Video Projects
        </h2>
        <div className="w-24 h-px bg-gradient-to-r from-transparent via-white to-transparent mx-auto opacity-60"></div>
      </div>
      
      {/* Scrollable container */}
      <div 
        ref={scrollRef}
        className={`overflow-x-auto overflow-y-hidden transition-opacity duration-500 ${isLoaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ 
          scrollbarWidth: 'none', 
          msOverflowStyle: 'none',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth'
        }}
      >
        <div 
          className="flex gap-12 md:gap-16 pb-12"
          style={{ 
            width: 'max-content'
          }}
        >
        {infiniteVideos.map((video, index) => {
          const videoKey = `${video.id}-${index}`;
          const isVisible = visibleVideos.has(videoKey);
          
          return (
            <div
              key={videoKey}
              ref={(el) => {
                if (el) videoRefs.current.set(videoKey, el);
              }}
              data-video-key={videoKey}
              className="flex-shrink-0 w-[500px] md:w-[650px] lg:w-[800px] group cursor-pointer transform transition-all duration-500 ease-out hover:scale-105"
              onClick={(e) => handleVideoClick(e, video)}
              onMouseEnter={() => setHoveredVideo(videoKey)}
              onMouseLeave={() => setHoveredVideo(null)}
            >
              {/* Large Video Container - California minimalism */}
              <div className="relative aspect-video bg-gradient-to-br from-gray-900 to-black overflow-hidden shadow-2xl">
                
                {/* Placeholder while loading */}
                {!loadedThumbnails.has(videoKey) && (
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-900 to-black">
                    <div className="text-center">
                      <div className="w-16 h-16 border-2 border-white/30 border-t-white/80 rounded-full animate-spin mb-4"></div>
                      <h3 className="text-xl font-light text-white/80">{video.title}</h3>
                      <p className="text-white/60">{video.company}</p>
                    </div>
                  </div>
                )}
                
                {/* Video Thumbnail - Only load when visible */}
                {isVisible && (
                  <video
                    src={video.thumbnailUrl}
                    className={`w-full h-full object-cover transition-opacity duration-700 ${
                      loadedThumbnails.has(videoKey) ? 'opacity-100' : 'opacity-0'
                    }`}
                    preload="metadata"
                    muted
                    playsInline
                    onLoadedMetadata={(e) => {
                      const videoElement = e.currentTarget;
                      // Seek to exactly 3 seconds for consistent thumbnails
                      videoElement.currentTime = Math.min(3, videoElement.duration);
                    }}
                    onSeeked={() => {
                      // Mark as loaded when seek is complete and frame is displayed
                      setLoadedThumbnails(prev => {
                        const newSet = new Set(prev);
                        newSet.add(videoKey);
                        return newSet;
                      });
                    }}
                    onError={(e) => {
                      console.warn('Failed to load video thumbnail:', video.thumbnailUrl);
                      // Still mark as loaded to remove spinner
                      setLoadedThumbnails(prev => {
                        const newSet = new Set(prev);
                        newSet.add(videoKey);
                        return newSet;
                      });
                    }}
                  />
                )}
                {/* Elegant overlay on hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out pointer-events-none">
                  
                  {/* Large Play button - California minimalist style */}
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-24 h-24 md:w-28 md:h-28 bg-white/15 backdrop-blur-md rounded-full flex items-center justify-center transform scale-75 group-hover:scale-100 transition-all duration-500 ease-out border border-white/30 shadow-2xl">
                      <FaPlay className="w-8 h-8 md:w-10 md:h-10 text-white ml-1 drop-shadow-lg" />
                    </div>
                  </div>

                  {/* Info overlay - larger text for bigger cards */}
                  <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10 transform translate-y-6 group-hover:translate-y-0 transition-all duration-500 ease-out">
                    <h3 className="text-white font-light text-2xl md:text-3xl lg:text-4xl mb-3 drop-shadow-lg line-clamp-2">
                      {video.title}
                    </h3>
                    <p className="text-white/80 text-lg md:text-xl font-light drop-shadow-lg">
                      {video.company}
                    </p>
                  </div>
                </div>

                {hoveredVideo === videoKey && loadedThumbnails.has(videoKey) && (
                  <div className="absolute inset-0 bg-black">
                    <video
                      src={video.thumbnailUrl}
                      className="w-full h-full object-cover transition-opacity duration-500"
                      autoPlay
                      muted
                      loop
                      playsInline
                      preload="none"
                      onError={(e) => {
                        console.warn('Failed to load video preview:', video.thumbnailUrl);
                        e.currentTarget.style.display = 'none';
                      }}
                    />
                  </div>
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {/* Custom styles for the California vibe */}
      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        .animate-fade-in {
          animation: fade-in 0.8s ease-out forwards;
        }
        
        /* Completely invisible scrolling */
        div::-webkit-scrollbar {
          display: none;
          width: 0;
          height: 0;
        }
        
        /* Firefox */
        div {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
      `}</style>
    </div>
  );
};

export default VideoBand;
